/**
 * @Page WareOrdr
 * @Description 실사재고 > 기준정보 > 창고등록 > 차수등록 팝업
 **/
import React, { useState, useEffect, useRef, useCallback } from "react";
import Ribbon from '@/components/organisms/Ribbon';
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import AlertDialog from '@/components/material-UI/AlertDialog';
import * as Common from '@/utils/Common';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;

function WareOrdr({ onClose, wrcd, vnt1 }) {

    const token = Common.getToken();
    const grdWareOrdr = useRef();

    const [message, setMessage] = useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    const [dlgMessage, setDlgMessage] = useState({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    const { openDlg, dlgTrans, dlgMsg } = dlgMessage;

    let [searchApi, setSearchApi] = useState(false);
    let [insertApi, setInsertApi] = useState(false);
    let [updateApi, setUpdateApi] = useState(false);
    let [deleteApi, setDeleteApi] = useState(false);

    const onClearApi = useCallback(async () => {
        setSearchApi(false);
        setInsertApi(false);
        setUpdateApi(false);
        setDeleteApi(false);
    }, []);

    /**
     * 마운트 시 차수 목록 조회
     */
    useEffect(() => {
        if (wrcd) {
            onSearch();
        }
    }, [wrcd]);


    /**
     * 조회
     */
    const onSearch = async () => {
        let para = `{"vnt1":"${vnt1}","wrcd":"${wrcd}"}`;
        let param = encodeURI(para);
        const query = SERVER_URL + "/rst01/ordr/search?query=" + param;
        setSearchApi(query);
    };


    /**
     * 신규 Row 추가 - 차수(ordr)는 현재 행 수 + 1 자동 부여
     */
    const onAddRow = async () => {
        // 현재 그리드의 차수(ordr) 목록에서 1부터 순서대로 빠진 번호를 먼저 채움
        // 예: 1,2,4,8 → 신규 추가 시 3 부여 / 1,2,3,4,8 → 5 부여
        let nextOrdr = 1;
        if (grdWareOrdr.current && grdWareOrdr.current.getRowAllData) {
            const allRows = grdWareOrdr.current.getRowAllData();
            const ordrSet = new Set(
                allRows.map(row => parseInt(row.ordr, 10)).filter(n => !isNaN(n))
            );
            while (ordrSet.has(nextOrdr)) nextOrdr++;
        }

        let newItems = { vand: vnt1, wrcd: wrcd, ordr: nextOrdr, gocd: "ADD" };
        grdWareOrdr.current.addRow(newItems, 0);
    };


    /**
     * 입력
     */
    const onInsert = async () => {
        let selectedData = grdWareOrdr.current.getSelectedNodes();

        if (selectedData.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택된 데이터가 없습니다." });
            return false;
        }

        // 사용여부 필수값 체크
        const hasEmptyOrdryn = selectedData.some(node => {
            const val = node.data.ordryn;
            return val === undefined || val === null || val === "";
        });
        if (hasEmptyOrdryn) {
            setMessage({ openAlert: true, severity: "warning", msg: "사용여부는 필수 입력값입니다." });
            return false;
        }

        const query = SERVER_URL + "/rst01/ordr/insert";
        setInsertApi(query);
    };


    /**
     * 수정
     */
    const onUpdate = async () => {
        let selectedData = grdWareOrdr.current.getSelectedNodes();

        if (selectedData.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "수정할 데이터를 선택해 주세요." });
            return false;
        }

        const query = SERVER_URL + "/rst01/ordr/update";
        setUpdateApi(query);
    };


    /**
     * 삭제
     */
    const onDelete = async () => {
        let selectedData = grdWareOrdr.current.getSelectedNodes();

        if (selectedData.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택한 데이터가 없습니다." });
            return false;
        }

        setDlgMessage({ openDlg: true, dlgTrans: "D", dlgMsg: "선택한 차수를 삭제하시겠습니까?" });
    };


    /**
     * AlertDialog 결과 처리
     */
    const AlertDialogResult = async (e) => {
        setDlgMessage({ openDlg: false, dlgTrans: "", dlgMsg: "" });

        if (e.result === "N") return;

        switch (e.Trans) {
            case "D":
                const query = SERVER_URL + "/rst01/ordr/delete";
                setDeleteApi(query);
                break;
            default:
                break;
        }
    };


    /**
     * Grid Column 정의
     */
    const columnDefs = [
        { headerName: "창고코드(h)", field: "wrcd", hide: true },
        { headerName: "업체코드(h)", field: "vand", hide: true },
        {
            headerName: "차수", field: "ordr",
            sortable: true, filter: true, flex: 1, minWidth: 100,
            editable: false,                    // 자동 부여 - 사용자 수정 불가
            headerClass: "",
            cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: false, hide: false,
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: "비고", field: "etc",
            sortable: true, filter: true, flex: 2, minWidth: 180,
            editable: true, headerClass: "editCellOnlySub",
            cellClass: ["nocheckbox", "alignL"],
            singleClickEdit: true, hide: false,
        },
        {
            headerName: "사용여부", field: "ordryn",
            sortable: true, filter: true, flex: 1, minWidth: 90,
            editable: true, headerClass: "editCellOnlySub",
            cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true, hide: false,
            cellEditor: "agSelectCellEditor",
            cellEditorParams: { values: ["Y", "N"] },
        },
    ];


    return (
        <>
            {/* Dialog FRAME */}
            <AlertDialog open={openDlg} trans={dlgTrans} message={dlgMsg} onClose={AlertDialogResult} />

            {/* Alert FRAME */}
            <CustomAlert
                open={openAlert} severity={severity} duration={3000} message={msg}
                onClose={() => { setMessage({ openAlert: false, severity: "", msg: "" }); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            />

            {/* 닫기 버튼 */}
            <button
                onClick={() => { onClose(); }}
                style={{ border: "none", float: "right", paddingRight: "0px", background: "transparent", cursor: "pointer" }}
            >
                <img src="/images/button/close_button.png" alt="close" />
            </button>

            {/* Ribbon */}
            <Ribbon
                newTabUnused={true}
                onInfoManual={false}
                onSearch={onSearch} onSearchName={null}
                onAddRow={onAddRow} onAddRowName={"신규"}
                onInsert={onInsert} onInsertName={null}
                onUpdate={onUpdate} onUpdateName={null}
                onDelete={onDelete} onDeleteName={null}
            >
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardLight, NanumGothic, sans-serif" }}>
                    차수등록 - [{wrcd}]
                </b>
            </Ribbon>

            {/* Grid */}
            <div className={"grid_type01"} style={{ marginTop: "10px" }}>
                <div className={"grid_wrap01"}>
                    <div className="grid_cont_box" style={{ height: "380px", width: "100%" }}>
                        <Grid
                            ref={grdWareOrdr}
                            gridId={"WAREORDR"}
                            columnDefs={columnDefs}
                            pagination={false}
                            paginationCsv={false}
                            paginationPageSize={"1000"}
                            rowMultiSelectWithClick={true}
                            suppressRowClickSelection={true}
                            rowSelection={"multiple"}
                            enableRangeSelection={false}
                            enableCellTextSelection={true}
                            isFirst={true}
                            displayNo={false}
                            searchApi={searchApi}
                            insertApi={insertApi}
                            updateApi={updateApi}
                            deleteApi={deleteApi}
                            onClearApi={onClearApi}
                            insertRefresh={onSearch}
                            updateRefresh={onSearch}
                            deleteRefresh={onSearch}
                            onCellValueChangedEvent={async (e) => { }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default WareOrdr;
